import { BONDING_CONFIG } from '../constants/horseConstants';

/**
 * Resolve image URL - converts relative /api/ paths to full backend URLs
 * @param {string} url - Image URL (can be relative or absolute)
 * @returns {string} Full image URL
 */
export const resolveImageUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL (http/https), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If it's a relative /api/ path, prepend the backend URL
  if (url.startsWith('/api/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}${url}`;
  }
  
  // For any other relative path, return as-is (browser will resolve)
  return url;
};

/**
 * Resolve thumbnail URL - converts image URLs to thumbnail URLs for faster loading
 * @param {string} url - Image URL (can be relative or absolute)
 * @returns {string} Thumbnail URL
 */
export const resolveThumbUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL (http/https), return as-is (no thumbnails for external)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Convert /api/media/xxx to /api/media/thumb/xxx
  if (url.startsWith('/api/media/') && !url.includes('/thumb/')) {
    const filename = url.replace('/api/media/', '');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}/api/media/thumb/${filename}`;
  }
  
  // If it's a relative /api/ path, prepend the backend URL
  if (url.startsWith('/api/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}${url}`;
  }
  
  // For any other relative path, return as-is (browser will resolve)
  return url;
};

/**
 * Calculate bonding level decay based on missed days
 * @param {number} currentLevel - Current bonding level (0-100)
 * @param {string} lastCareDate - Last date care was performed (YYYY-MM-DD)
 * @returns {number} New bonding level after decay
 */
export const calculateBondingDecay = (currentLevel, lastCareDate) => {
  if (!lastCareDate || currentLevel === 0) {
    return currentLevel;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lastDate = new Date(lastCareDate);
  lastDate.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 0) {
    return currentLevel;
  }

  // Apply 10% decay per missed day
  let newLevel = currentLevel;
  for (let i = 0; i < daysDiff; i++) {
    newLevel = newLevel - (newLevel * (BONDING_CONFIG.DECAY_PERCENTAGE / 100));
  }

  return Math.max(BONDING_CONFIG.MIN_LEVEL, Math.floor(newLevel));
};

/**
 * Calculate bonding level increase based on tasks completed
 * @param {number} currentLevel - Current bonding level (0-100)
 * @param {number} tasksCompleted - Number of tasks completed
 * @returns {number} New bonding level after increase
 */
export const calculateBondingIncrease = (currentLevel, tasksCompleted) => {
  const increase = tasksCompleted * BONDING_CONFIG.POINTS_PER_TASK;
  const newLevel = currentLevel + increase;
  return Math.min(BONDING_CONFIG.MAX_LEVEL, newLevel);
};

/**
 * Count total number of daily care tasks
 * @param {object} dailyCareChecklist - Daily care checklist object
 * @returns {number} Total number of tasks
 */
export const countTotalDailyTasks = (dailyCareChecklist) => {
  if (!dailyCareChecklist) return 0;
  
  let total = 0;
  Object.values(dailyCareChecklist).forEach(category => {
    if (typeof category === 'object') {
      total += Object.keys(category).length;
    }
  });
  return total;
};

/**
 * Count completed daily care tasks
 * @param {object} dailyCareChecklist - Daily care checklist object
 * @returns {number} Number of completed tasks
 */
export const countCompletedDailyTasks = (dailyCareChecklist) => {
  if (!dailyCareChecklist) return 0;
  
  let completed = 0;
  Object.values(dailyCareChecklist).forEach(category => {
    if (typeof category === 'object') {
      Object.values(category).forEach(value => {
        if (value === true) completed++;
      });
    }
  });
  return completed;
};

/**
 * Check if enough daily tasks completed to prevent bonding decay
 * @param {object} dailyCareChecklist - Daily care checklist object
 * @returns {boolean} True if minimum percentage is met
 */
export const hasMetMinimumDailyCare = (dailyCareChecklist) => {
  const total = countTotalDailyTasks(dailyCareChecklist);
  const completed = countCompletedDailyTasks(dailyCareChecklist);
  
  if (total === 0) return false;
  
  const percentage = (completed / total) * 100;
  return percentage >= BONDING_CONFIG.MIN_COMPLETION_PERCENTAGE;
};

/**
 * Get bonding level display info (color, label, etc.)
 * @param {number} level - Bonding level (0-100)
 * @returns {object} Display info
 */
export const getBondingLevelDisplay = (level) => {
  if (level >= 80) {
    return {
      label: 'Excellent Bond',
      color: '#10B981', // green
      emoji: '💚',
      description: 'Your mustang trusts you completely'
    };
  } else if (level >= 60) {
    return {
      label: 'Strong Bond',
      color: '#3B82F6', // blue
      emoji: '💙',
      description: 'Your mustang is comfortable with you'
    };
  } else if (level >= 40) {
    return {
      label: 'Developing Bond',
      color: '#F59E0B', // amber
      emoji: '🧡',
      description: 'Your mustang is warming up to you'
    };
  } else if (level >= 20) {
    return {
      label: 'Fragile Bond',
      color: '#EF4444', // red
      emoji: '❤️',
      description: 'Your mustang is still cautious'
    };
  } else {
    return {
      label: 'New Bond',
      color: '#9CA3AF', // gray
      emoji: '🤍',
      description: 'Just starting your journey together'
    };
  }
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date
 */
export const getTodayDateString = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Check if daily care needs to be reset (new day)
 * @param {string} lastCareDate - Last care date (YYYY-MM-DD)
 * @returns {boolean} True if new day
 */
export const shouldResetDailyCare = (lastCareDate) => {
  const today = getTodayDateString();
  return lastCareDate !== today;
};

/**
 * Create a new empty daily care checklist
 * @returns {object} Empty checklist
 */
export const createEmptyDailyCareChecklist = () => {
  return {
    grain: { am: false, pm: false },
    supplements: { am: false, pm: false },
    hay: { am: false, pm: false },
    water: { am_clean: false, pm_clean: false },
    grooming: {
      currying_face: false,
      currying_body: false,
      currying_legs: false,
      brush_face: false,
      brush_body: false,
      brush_legs: false,
      comb_mane_forelock: false,
      brush_tail: false,
      pick_feet: false
    }
  };
};

/**
 * Count total gentling tasks completed
 * @param {object} gentling - Gentling object
 * @returns {number} Number of completed tasks
 */
export const countCompletedGentlingTasks = (gentling) => {
  if (!gentling) return 0;
  
  let completed = 0;
  Object.values(gentling).forEach(category => {
    if (typeof category === 'object') {
      Object.values(category).forEach(value => {
        if (value === true) completed++;
      });
    }
  });
  return completed;
};
